import { Decoration, DecorationSet } from '@milkdown/kit/prose/view';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import { $prose } from '@milkdown/kit/utils';
import { linkSchema } from '@milkdown/kit/preset/commonmark';

import type { DocumentIssue } from '../types';

export const documentIssuesDecoKey = new PluginKey('documentIssuesDeco');

/** Action strings matching health check (Platform HealthCheckAction enum values) */
const BROKEN_ARTICLE_LINKS = 'Broken article links';
const BROKEN_IMAGE_LINKS = 'Broken image links';
const INAPPROPRIATE_CONTENT = 'Inappropriate content';
const GRAMMATICAL_ERRORS = 'Grammatical errors';

/**
 * Normalize URL for comparison: strip blk param and trailing slash
 */
function normalizeUrlForComparison(href: string): string | null {
  try {
    const url = new URL(href, 'https://dummy.invalid');
    url.searchParams.delete('blk');

    return url.toString().replace(/\/$/, '');
  } catch {
    return null;
  }
}

/**
 * ProseMirror plugin that decorates document content based on health check issues.
 * - Broken article links: link marks whose href (normalized) is in the issue list
 * - Broken image links: image nodes whose src (normalized) is in the issue list
 * - Inappropriate content / Grammatical errors: text nodes containing issue.issue
 */
export function documentIssuesDecorationPlugin(issues: DocumentIssue[] | undefined) {
  const list = issues ?? [];
  const brokenArticleUrls = new Set(
    list
      .filter((i) => i.action === BROKEN_ARTICLE_LINKS)
      .map((i) => normalizeUrlForComparison(i.issue))
      .filter((u): u is string => u != null),
  );
  const brokenImageUrls = new Set(
    list
      .filter((i) => i.action === BROKEN_IMAGE_LINKS)
      .map((i) => normalizeUrlForComparison(i.issue))
      .filter((u): u is string => u != null),
  );
  const inappropriateTexts = list
    .filter((i) => i.action === INAPPROPRIATE_CONTENT)
    .map((i) => i.issue)
    .filter(Boolean);
  const grammaticalTexts = list
    .filter((i) => i.action === GRAMMATICAL_ERRORS)
    .map((i) => i.issue)
    .filter(Boolean);

  return $prose((ctx) => {
    const linkType = linkSchema.type(ctx);

    return new Plugin({
      key: documentIssuesDecoKey,
      props: {
        decorations: (state) => {
          const decorations: Decoration[] = [];
          const doc = state.doc;

          doc.descendants((node, pos) => {
            // Broken article links: link mark with href in list
            if (node.isInline && node.marks.length) {
              const linkMark = node.marks.find((m) => m.type === linkType);
              if (linkMark?.attrs?.href && brokenArticleUrls.size) {
                const href = linkMark.attrs.href as string;
                const normalized = normalizeUrlForComparison(href);
                if (normalized != null && brokenArticleUrls.has(normalized)) {
                  decorations.push(
                    Decoration.inline(pos, pos + node.nodeSize, {
                      class: 'document-issue document-issue-broken-link',
                    }),
                  );
                }
              }
            }

            // Broken image links: image-block, image-inline, or image node with src in list
            if (
              (node.type.name === 'image-block' || node.type.name === 'image-inline' || node.type.name === 'image') &&
              node.attrs?.src &&
              brokenImageUrls.size
            ) {
              const src = node.attrs.src as string;
              const normalized = normalizeUrlForComparison(src);
              if (normalized != null && brokenImageUrls.has(normalized)) {
                decorations.push(
                  Decoration.node(pos, pos + node.nodeSize, {
                    class: 'document-issue document-issue-broken-image',
                  }),
                );
              }
            }

            // Inappropriate content / Grammatical errors: text nodes containing issue.issue
            if (node.isText && node.text) {
              const text = node.text;
              for (const search of [...inappropriateTexts, ...grammaticalTexts]) {
                if (!search) continue;
                let from = 0;
                let idx: number;
                const isInappropriate = inappropriateTexts.includes(search);
                const cls = isInappropriate ? 'document-issue document-issue-inappropriate' : 'document-issue document-issue-grammatical';
                while ((idx = text.indexOf(search, from)) !== -1) {
                  const start = pos + idx;
                  const end = start + search.length;
                  decorations.push(Decoration.inline(start, end, { class: cls }));
                  from = idx + 1;
                }
              }
            }
          });

          return DecorationSet.create(doc, decorations);
        },
      },
    });
  });
}
